import classNames from 'classnames';
import { useState } from 'react';

import { useRouteContext } from 'src/hooks/useRouteContext';

import { CoreHeroData } from 'src/types/components';

import Image from 'components/BaseHelpers/Image';
import PillCTA from 'components/BaseHelpers/PillCTA';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import styles from './CoreHero.module.scss';

export type CoreHeroProps = CoreHeroData;

type RenderImageProps = {
  media: CoreHeroProps['fields']['media'];
  variant: CoreHeroProps['fields']['variant'];
};
const RenderImage = ({ media, variant }: RenderImageProps) => {
  const [loaded, setLoaded] = useState(false);
  if (variant === 'noMedia') {
    return null;
  }

  if (!media || !media.image) {
    return null;
  }

  const { image, videoURL, imageAlignment } = media;

  const alignment = imageAlignment;

  return (
    <div className={styles.imageWrapper}>
      <div className={styles.imageInnerWrapper}>
        <Image
          className={classNames(styles.image, styles[`image--alignment-${alignment}`])}
          {...image}
          fill
          priority
        />
        {videoURL && (
          <video
            disablePictureInPicture
            loop
            preload="auto"
            autoPlay
            muted
            className={classNames(
              styles.videoEmbed,
              styles[`videoEmbed--${loaded ? 'loaded' : 'loading'}`]
            )}
            onPlay={() => setLoaded(true)}
          >
            <source src={videoURL} />
          </video>
        )}
      </div>
    </div>
  );
};

const CoreHero = ({
  fields: { variant, colorTheme, header, primaryCallToAction, secondaryCallToAction, media },
}: CoreHeroProps): JSX.Element | null => {
  const { eyebrow, subheading, heading, description } = header;

  const primaryCTAReversed =
    (variant === 'noMedia' || variant == 'standard') && colorTheme === 'dark';

  const { routeData } = useRouteContext();

  const hasCTAs = primaryCallToAction?.href || secondaryCallToAction?.href;

  return (
    <section
      className={classNames(
        styles.hero,
        styles[variant],
        'spacer',
        'spacer--solid',
        styles[`theme--${colorTheme}`]
      )}
    >
      <div className={classNames(styles.innerWrapper, 'global__wrapper--1920')}>
        <div className={styles.copyWrapper}>
          {!!eyebrow && <Text tag="p" className={styles.eyebrow} field={eyebrow} />}
          <div className={styles.innerCopyWrapper}>
            <div className={styles.header}>
              {routeData?.route.path === '/' ? (
                <Text tag="h2" className={styles.heading} field={heading} />
              ) : (
                <Text tag="h1" className={styles.heading} field={heading} />
              )}
              {!!subheading && <Text tag="p" className={styles.subheading} field={subheading} />}
            </div>
            {(!!description || hasCTAs) && (
              <div className={styles.ctas}>
                {!!description && <RichText field={description} className={styles.description} />}
                {hasCTAs && (
                  <ul className={styles.ctaList}>
                    {primaryCallToAction?.href && (
                      <li>
                        <PillCTA reversed={primaryCTAReversed} tag="a" {...primaryCallToAction} />
                      </li>
                    )}
                    {secondaryCallToAction?.href && (
                      <li>
                        <PillCTA
                          tag="a"
                          variant="yellow"
                          keepBorderOnHover={colorTheme === 'dark'}
                          {...secondaryCallToAction}
                        />
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
        <RenderImage media={media} variant={variant} />
      </div>
    </section>
  );
};

export default CoreHero;
